import React, { useEffect, useState } from 'react'
import { cookies } from '../../utils/cookie'
import { jwtDecode } from 'jwt-decode'
import Swal from 'sweetalert2'
import { useDispatch, useSelector } from 'react-redux'
import { loginThunk } from '../../redux/features/auth/authThunks'
import { selectUser, selectIsLoggedIn } from '../../redux/features/auth/authSelectors'
import {
  Box,
  Button,
  Checkbox,
  Typography,
  TextField,
  Container,
  Paper,
  useTheme,
  InputAdornment,
  IconButton,
  Stack,
  Divider,
  alpha,
  LinearProgress,
} from '@mui/material'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Activity,
  Shield,
  Zap,
  TrendingUp,
} from 'lucide-react'
import { login } from '../../api/auth/login/index'
import { useFormik } from 'formik'
import { loginFormSchema } from '../../utils/formSchema/formSchemas'

const ModernLogin = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const userRedux = useSelector(selectUser)
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const loading = useSelector((state) => state.auth.loading)
  const error = useSelector((state) => state.auth.error)
  const navigate = useNavigate()

  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [vaidateOnChangeState, setValidateOnChangeState] = useState(false)
  const [vaidateOnBlurState, setValidateOnBlurState] = useState(true)

  useEffect(() => {
    setRememberMe(cookies.get('rememberMe'))
  }, [])

  const handleLogin = async (values) => {
    setIsLoading(true)
    try {
      const resp = await login(values.email, values.password)

      // Set cookies
      cookies.set('jwt-access', resp.data.access.token)
      cookies.set('jwt-access-expires', resp.data.access.expires)
      cookies.set('jwt-refresh', resp.data.refresh.token)
      cookies.set('jwt-refresh-expires', resp.data.refresh.expires)

      if (rememberMe) {
        cookies.set('email', values.email, { path: '/', maxAge: 60 * 60 * 24 * 30 })
        cookies.set('password', values.password, { path: '/', maxAge: 60 * 60 * 24 * 30 })
        cookies.set('rememberMe', rememberMe, { path: '/', maxAge: 60 * 60 * 24 * 30 })
      } else {
        cookies.remove('email')
        cookies.remove('password')
        cookies.remove('rememberMe')
      }

      const decodedToken = jwtDecode(resp.data.access.token)

      // Success notification
      Swal.fire({
        title: 'Giriş Başarılı!',
        text: 'Hoş geldiniz',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })

      // Navigate based on role
      setTimeout(() => {
        if (decodedToken.role === 'user') {
          navigate('/user/monitors')
        } else if (decodedToken.role === 'admin') {
          navigate('/admin')
        }
        dispatch(loginThunk())
      }, 1000)
    } catch (err) {
      setIsLoading(false)
      Swal.fire({
        title: 'Giriş Başarısız',
        text: err.response?.data?.message || 'Bir hata oluştu',
        icon: 'error',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      })
    }
  }

  const { values, errors, handleChange, handleSubmit } = useFormik({
    initialValues: {
      email: cookies.get('email') || '',
      password: cookies.get('password') || '',
    },
    onSubmit: handleLogin,
    validationSchema: loginFormSchema,
    validateOnChange: vaidateOnChangeState,
    validateOnBlur: vaidateOnBlurState,
  })

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setValidateOnChangeState(true)
      setValidateOnBlurState(false)
    }
  }, [errors])

  const features = [
    { icon: Activity, text: 'Real-time Monitoring', color: '#10b981' },
    { icon: Shield, text: 'SSL Tracking', color: '#3b82f6' },
    { icon: Zap, text: 'Instant Alerts', color: '#f59e0b' },
    { icon: TrendingUp, text: 'Uptime Analytics', color: '#8b5cf6' },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        background: theme.palette.background.default,
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        {/* Gradient Orbs */}
        {[...Array(3)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
              x: [0, 50, 0],
              y: [0, -30, 0],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 2,
            }}
            sx={{
              position: 'absolute',
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.15
              )}, transparent)`,
              filter: 'blur(60px)',
              top: `${20 + i * 30}%`,
              left: `${10 + i * 30}%`,
            }}
          />
        ))}

        {/* Grid Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(${alpha(theme.palette.primary.main, 0.1)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)} 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            opacity: 0.3,
          }}
        />

        {/* Scanning Line */}
        <Box
          component={motion.div}
          animate={{
            y: ['0vh', '100vh'],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear',
          }}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
            opacity: 0.3,
          }}
        />
      </Box>

      {/* Left Side - Branding */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 6,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          sx={{ maxWidth: 500 }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              RahatUp
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: alpha('#fff', 0.8),
                fontWeight: 600,
                mb: 1,
              }}
            >
              Website Monitoring Platform
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: alpha('#fff', 0.6),
                lineHeight: 1.8,
              }}
            >
              Sistemlerinizi 7/24 izleyin. Kesinti olmadan, hızlı uyarılarla,
              güvenle işlerinize devam edin.
            </Typography>
          </Box>

          {/* Features */}
          <Stack spacing={3} sx={{ mt: 6 }}>
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <Box
                  key={idx}
                  component={motion.div}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    background: alpha(theme.palette.background.paper, 0.3),
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(10px)',
                      background: alpha(theme.palette.background.paper, 0.5),
                      border: `1px solid ${alpha(feature.color, 0.3)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      background: alpha(feature.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={24} color={feature.color} />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha('#fff', 0.9),
                      fontWeight: 600,
                    }}
                  >
                    {feature.text}
                  </Typography>
                </Box>
              )
            })}
          </Stack>

          {/* Stats */}
          <Box
            sx={{
              mt: 6,
              display: 'flex',
              gap: 4,
            }}
          >
            {[
              { label: 'Monitörler', value: '10K+' },
              { label: 'Uptime', value: '99.9%' },
              { label: 'Kullanıcılar', value: '5K+' },
            ].map((stat, idx) => (
              <Box key={idx}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    color: theme.palette.primary.main,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha('#fff', 0.6),
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Right Side - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, md: 6 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.background.paper,
                0.9
              )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Hoş Geldiniz
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: alpha('#fff', 0.6),
                }}
              >
                Hesabınıza giriş yapın ve sistemlerinizi izlemeye başlayın
              </Typography>
            </Box>

            {/* Form */}
            <Stack spacing={3}>
              {/* Email Field */}
              <Box>
                <TextField
                  id="email"
                  fullWidth
                  label="E-posta"
                  placeholder="ornek@email.com"
                  value={values.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail size={20} color={theme.palette.primary.main} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.default, 0.5),
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              {/* Password Field */}
              <Box>
                <TextField
                  id="password"
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label="Şifre"
                  placeholder="••••••••"
                  value={values.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock size={20} color={theme.palette.primary.main} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            color: alpha('#fff', 0.6),
                            '&:hover': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.default, 0.5),
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              {/* Remember Me & Forgot Password */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: alpha(theme.palette.primary.main, 0.5),
                      '&.Mui-checked': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: alpha('#fff', 0.7),
                    }}
                  >
                    Beni Hatırla
                  </Typography>
                </Box>
                <Button
                  onClick={() => navigate('/forgot-password')}
                  sx={{
                    color: theme.palette.primary.main,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  Şifremi Unuttum
                </Button>
              </Box>

              {/* Login Button */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: '#000',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.primary.main, 0.5),
                    color: alpha('#000', 0.5),
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </Button>

              {isLoading && <LinearProgress sx={{ borderRadius: 1 }} />}

              {/* Divider */}
              <Divider
                sx={{
                  my: 2,
                  '&::before, &::after': {
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha('#fff', 0.5),
                    px: 2,
                  }}
                >
                  VEYA
                </Typography>
              </Divider>

              {/* Register Link */}
              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#fff', 0.6),
                    display: 'inline',
                  }}
                >
                  Hesabınız yok mu?{' '}
                </Typography>
                <Button
                  onClick={() => navigate('/register')}
                  sx={{
                    color: theme.palette.primary.main,
                    textTransform: 'none',
                    fontWeight: 700,
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      background: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Kayıt Ol
                </Button>
              </Box>
            </Stack>
          </Paper>

          {/* Security Badge */}
          <Box
            sx={{
              mt: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.success.main, 0.1),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              }}
            >
              <Shield size={16} color={theme.palette.success.main} />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.success.main,
                  fontWeight: 600,
                }}
              >
                256-bit SSL Şifreli Bağlantı
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

export default ModernLogin